import i18n from '../i18n';

export type CurrencyCode = 'KRW' | 'USD';

type FormatDisplayCurrencyOptions = {
  useSymbol?: boolean;
};

export const extractDigits = (raw: string) => raw.replaceAll(/[^0-9]/g, '');

export const parseCurrencyNumber = (raw: string) => {
  const digits = extractDigits(raw);
  if (!digits) return 0;
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
};

export const formatCurrencyInput = (raw: string, currency: CurrencyCode) => {
  const digits = extractDigits(raw);
  if (!digits) return '';
  const n = Number(digits);
  if (!Number.isFinite(n)) return '';
  const formatted = n.toLocaleString('en-US');
  return currency === 'KRW' ? `₩${formatted}` : `$${formatted}`;
};

export const normalizeCurrencyCode = (currency?: string): CurrencyCode => (
  String(currency ?? '').toUpperCase() === 'USD' ? 'USD' : 'KRW'
);

export const formatDisplayCurrency = (
  value: number,
  currency?: string,
  options?: FormatDisplayCurrencyOptions,
) => {
  const amount = Number.isFinite(value) ? value : 0;
  const currencyCode = normalizeCurrencyCode(currency);
  const locale = currencyCode === 'USD' ? 'en-US' : 'ko-KR';
  // 부호는 통화 기호 앞에 표기한다 (예: ₩-5,000 이 아니라 -₩5,000).
  // 0(및 -0)은 부호 없이 ₩0 / $0 으로 표기한다.
  const sign = amount < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat(locale).format(Math.abs(amount));
  const symbol = currencyCode === 'USD' ? '$' : '₩';

  return `${sign}${symbol}${formatted}`;
};

export const formatKoreanCurrency = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return '';
  const isEnglish = /^en/i.test(i18n.resolvedLanguage || i18n.language || '');
  if (isEnglish) return `₩${Math.floor(value).toLocaleString('en-US')} won`;

  const wonLabel = isEnglish ? 'won' : i18n.t('common.units.won');
  const units = [
    { value: 1e12, label: i18n.t('common.currency.compact.trillion') },
    { value: 1e8, label: i18n.t('common.currency.compact.hundredMillion') },
    { value: 1e4, label: i18n.t('common.currency.compact.tenThousand') },
  ];
  let remaining = Math.floor(value);
  const parts: string[] = [];

  for (const unit of units) {
    const part = Math.floor(remaining / unit.value);
    if (part > 0) {
      parts.push(isEnglish ? `${part.toLocaleString('en-US')} ${unit.label}` : `${part.toLocaleString('ko-KR')}${unit.label}`);
      remaining %= unit.value;
    }
  }

  if (parts.length === 0) {
    return isEnglish ? `${remaining.toLocaleString('en-US')} ${wonLabel}` : `${remaining.toLocaleString('ko-KR')}${wonLabel}`;
  }

  if (remaining > 0) {
    parts.push(remaining.toLocaleString(isEnglish ? 'en-US' : 'ko-KR'));
  }

  return isEnglish ? `${parts.join(' ')} ${wonLabel}` : `${parts.join(' ')}${wonLabel}`;
};
